import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection, useDocument } from '@/hooks/firestore';
import { Card, EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { RatingStars } from '@/components/ui/RatingStars';
import { toast } from '@/components/ui/Toast';
import { callAcceptOffer, callCancelRequest, callExtendRequest } from '@/lib/callables';
import { CATEGORY_LABELS, PAYMENTS_ENABLED, formatZAR, type OfferDoc, type RequestDoc } from '@makhe/shared';
import { REQUEST_BADGE } from './Requests';

export default function RequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const reqRef = requestId ? doc(db, 'requests', requestId) : null;
  const { data: request, loading } = useDocument<RequestDoc>(reqRef);

  const offersQ = useMemo(
    () => (requestId ? query(collection(db, 'requests', requestId, 'offers'), orderBy('createdAt', 'desc')) : null),
    [requestId],
  );
  const { data: offers } = useCollection<OfferDoc>(offersQ);

  const [accepting, setAccepting] = useState<OfferDoc | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) return <LoadingSkeleton className="m-6 h-40" />;
  if (!request) return <p className="p-6 font-display text-2xl">Not found.</p>;

  const visibleOffers = offers.filter((o) => o.status === 'pending' || o.status === 'accepted');
  const isOpen = request.status === 'open';

  const accept = async () => {
    if (!accepting) return;
    setBusy(true);
    try {
      // Escrow-only (D-022): the server always creates an escrow-held booking.
      const res = await callAcceptOffer({ requestId: request.id, offerId: accepting.id, paymentTiming: 'now', origin: window.location.origin });
      setAccepting(null);
      if (res.data.paymentUrl) {
        window.location.assign(res.data.paymentUrl);
      } else {
        toast.success('Offer accepted — booking created.');
        navigate(`/bookings/${res.data.bookingId}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const wrap = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-6 py-8">
      <Link to="/requests" className="text-sm text-makhe-muted">← My requests</Link>

      <div className="mt-3 flex items-center gap-2">
        <span className="eyebrow">{CATEGORY_LABELS[request.category]}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${REQUEST_BADGE[request.status].cls}`}>
          {REQUEST_BADGE[request.status].label}
        </span>
      </div>
      <h1 className="mt-2 font-display text-3xl leading-tight">{request.title}</h1>
      {request.description && (
        <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-makhe-ink/85">{request.description}</p>
      )}

      {request.referenceImageURLs?.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {request.referenceImageURLs.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-md bg-makhe-line/40">
              <img src={url} alt="Reference" loading="lazy" className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      )}

      <Card className="mt-4 space-y-1 text-sm">
        {request.budgetHintZARcents != null && (
          <p><strong>Budget hint:</strong> {formatZAR(request.budgetHintZARcents)}</p>
        )}
        <p>
          <strong>When:</strong>{' '}
          {request.preferredWindow?.flexible
            ? 'Flexible'
            : request.preferredWindow?.start?.toDate?.().toLocaleString?.() ?? 'Flexible'}
        </p>
        <p><strong>Where:</strong> {request.location.type.replace('_', ' ')}{request.location.residence && ` · ${request.location.residence}`}</p>
      </Card>

      {request.status === 'matched' && request.bookingId && (
        <Link to={`/bookings/${request.bookingId}`}>
          <Button className="mt-4 w-full">View your booking →</Button>
        </Link>
      )}

      {isOpen && (
        <div className="mt-4 flex gap-2">
          <Button
            variant="ghost"
            loading={busy}
            onClick={() => wrap(() => callExtendRequest({ requestId: request.id }), 'Extended by 5 days.')}
          >
            Keep it up (+5 days)
          </Button>
          <Button
            variant="ghost"
            loading={busy}
            onClick={() => wrap(() => callCancelRequest({ requestId: request.id }), 'Request cancelled.')}
          >
            Cancel request
          </Button>
        </div>
      )}

      {/* Offers */}
      <div className="mt-8">
        <div className="flex items-baseline justify-between border-b border-makhe-line pb-3">
          <p className="eyebrow">Offers</p>
          <p className="font-display text-sm italic text-makhe-muted">
            {visibleOffers.length} offer{visibleOffers.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="mt-4 space-y-3">
          {visibleOffers.length === 0 && (
            <EmptyState title="No offers yet" description="Creators in your category have been notified. Check back soon." />
          )}
          {visibleOffers.map((o) => (
            <Card key={o.id} className={o.status === 'accepted' ? 'border-makhe-forest' : ''}>
              <div className="flex items-center gap-3">
                {o.sellerSnapshot.photoURL ? (
                  <img src={o.sellerSnapshot.photoURL} alt={o.sellerSnapshot.displayName} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-makhe-brand text-makhe-on">
                    {o.sellerSnapshot.displayName.charAt(0)}
                  </span>
                )}
                <div className="flex-1">
                  <Link to={`/seller/${o.sellerId}`} className="font-medium">{o.sellerSnapshot.displayName}</Link>
                  <div className="flex items-center gap-1 text-xs text-makhe-muted">
                    <RatingStars value={o.sellerSnapshot.averageRating} size="sm" />
                    <span>({o.sellerSnapshot.totalReviews})</span>
                  </div>
                </div>
                <p className="font-mono text-lg">{formatZAR(o.priceZARcents)}</p>
              </div>
              <p className="mt-2 text-xs text-makhe-muted">
                {o.proposedStart?.toDate?.().toLocaleString?.() ?? o.availabilityNote ?? 'Flexible on timing'}
                {o.durationMinutes ? ` · ${o.durationMinutes} min` : ''}
              </p>
              {o.message && <p className="mt-2 text-sm text-makhe-ink/85">{o.message}</p>}
              {o.status === 'accepted' ? (
                <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-makhe-forest">Accepted ✓</p>
              ) : (
                isOpen && (
                  <Button className="mt-3 w-full" variant="ink" onClick={() => setAccepting(o)}>
                    Accept this offer
                  </Button>
                )
              )}
            </Card>
          ))}
        </div>
      </div>

      <Modal open={!!accepting} onClose={() => setAccepting(null)} title="Accept offer" size="md">
        {accepting && (
          <div className="space-y-4">
            <p className="text-sm text-makhe-ink/80">
              Accept <strong>{accepting.sellerSnapshot.displayName}</strong>'s offer of{' '}
              <strong>{formatZAR(accepting.priceZARcents)}</strong>? This creates a booking and declines the other offers.
            </p>
            {PAYMENTS_ENABLED && (
              <p className="rounded-md border border-makhe-line bg-makhe-paper p-3 text-xs text-makhe-muted">
                Escrow-protected: we hold your payment until you confirm the service is done.
              </p>
            )}
            <div className="grid gap-2">
              <Button variant="primary" loading={busy} onClick={() => accept()}>
                {PAYMENTS_ENABLED ? 'Accept & pay (escrow) →' : 'Accept offer →'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
